/*
 * Our router config is not installed in the entrypoint because different
 * Root.vue "single page applications" use different routing.
 * In an ideal world, these root components would have their own entrypoints
 * like normal single page applications and set up all routing there. However,
 * we have to wait until the root component is created from our shared
 * entrypoint and then activate the router, which is what "useRouter" below
 * does.
 */

import { waitForPageReadiness } from "@/utils/PageReadinessDetector";
import type { App } from "vue";
import { createRouter, createWebHashHistory, createWebHistory, type Router, type RouteRecordRaw } from "vue-router";

const routerByApp = new WeakMap<App, Router>();

function createAppRouter(app: App, routes: RouteRecordRaw[], mode: "hash" | "html5" = "hash") {
	// Cache per-app routers so repeated calls (HMR or remounts) don't re-install the router.
	const existingRouter = routerByApp.get(app);
	if (existingRouter) {
		return existingRouter;
	}

	const router = createRouter({
		history: mode === "html5" ? createWebHistory() : createWebHashHistory(),
		routes,
		// Custom Scroll Behavior:
		// 1. For general navigation, we default to the top of the page.
		// While restoring to 'savedPosition' is ideal,  components like
		// 'InfiniteLoadingList' only render content as the user scrolls.
		// Since we cannot jump to a position that hasn't loaded yet,
		// we always reset to the top to ensure a consistent experience.
		//
		// 2. Hash navigation: scroll to the anchor target within the page.
		// We use `waitForPageReadiness()` to avoid scrolling while layout
		// is still jumping (CLS) on initial load. Once we fix the current CLS issue,
		// and the layout becomes more stable, we can get rid of this hack.
		async scrollBehavior(to) {
			if (to.hash) {
				await waitForPageReadiness();

				const target = document.querySelector(to.hash);
				if (target) {
					const scrollMarginTop = parseFloat(getComputedStyle(target).scrollMarginTop || "0");
					return {
						el: to.hash,
						top: scrollMarginTop,
					};
				}

				return {
					el: to.hash,
				};
			}

			return { top: 0 };
		},
	});

	app.use(router);
	routerByApp.set(app, router);
	return router;
}

export function useRouter(app: App, routes: RouteRecordRaw[]): Router {
	return createAppRouter(app, routes);
}

export function useHtml5ModeRouter(app: App, routes: RouteRecordRaw[]): Router {
	return createAppRouter(app, routes, "html5");
}
